/**
 * Lo que el chat de un canal de la Cloud API hace distinto (tarea 3.6), en puro: por qué el compose no
 * puede mandar, qué significa cada código de error del envío, y cómo se refleja un mensaje borrado.
 */
import type { TimelineRow } from "@/services/conversations.api";

/** Por qué no sale un mensaje desde el panel. Las notas internas siguen disponibles en todos. */
export type MotivoDeBloqueo = "restringido" | "desconectado" | "pausado" | "ventana";

/**
 * El motivo que el compose muestra, o `null` si se puede mandar. La restricción gana porque es lo que
 * explica un canal que figura conectado; la ventana va última porque es la única que se resuelve sola,
 * cuando el contacto escribe.
 */
export function motivoDeBloqueo(s: {
  connected: boolean;
  restricted: boolean;
  /** El envío por la Cloud API está pausado en este ambiente (D31). */
  paused: boolean;
  /** La ventana de 24 h (D27): `true` también en un canal que no la tiene. */
  windowOpen: boolean;
}): MotivoDeBloqueo | null {
  if (s.restricted) return "restringido";
  if (!s.connected) return "desconectado";
  if (s.paused) return "pausado";
  if (!s.windowOpen) return "ventana";
  return null;
}

/** El resumen de una línea, para el compose de nota y para quien sólo lee. */
export const RESUMEN_DEL_BLOQUEO: Record<MotivoDeBloqueo, string> = {
  restringido: "Número restringido",
  desconectado: "Canal sin conexión",
  pausado: "Envío pausado",
  ventana: "Fuera de la ventana de 24 h",
};

/** Lo que un error del envío le cambia al chat, por su código. */
export interface EfectoDelError {
  /** La ventana cerró: el compose se bloquea con la fecha que diga el backend, o sin fecha. */
  ventanaCerrada?: { expiresAt: string | null };
  /** D31: el envío por la Cloud API está pausado. */
  pausado?: boolean;
  /** D2: Meta rechazó el mensaje por el método de pago de la cuenta. */
  pago?: boolean;
  /** El estado del canal cambió: hay que pedirle a la bandeja el canal de nuevo. */
  refrescarCanal?: boolean;
  /** Un aviso, no un error: el panel no falló, es una regla de WhatsApp. */
  esAviso?: boolean;
}

export function efectoDelError(e: unknown): EfectoDelError {
  const data = (e as { response?: { data?: { code?: string; expiresAt?: string | null } } })?.response?.data;
  switch (data?.code) {
    case "OUTSIDE_SERVICE_WINDOW":
      return { ventanaCerrada: { expiresAt: data.expiresAt ?? null }, esAviso: true };
    case "CLOUD_SENDING_DISABLED":
      return { pausado: true };
    case "PAYMENT_REQUIRED":
      return { pago: true };
    // #177: la restricción, o una desconexión de Meta, llegó entre que se dibujó el chat y el envío.
    case "ACCOUNT_RESTRICTED":
    case "CHANNEL_DISCONNECTED":
      return { refrescarCanal: true };
    default:
      return {};
  }
}

/**
 * Un acuse que falla después de aceptado dice lo mismo que un error del envío, con el código de Meta:
 * `131047` es la ventana y `131042` el método de pago.
 */
export function efectoDelAcuseFallido(errorCode: string | null | undefined, ahora = new Date()): EfectoDelError {
  if (errorCode === "131047") return { ventanaCerrada: { expiresAt: ahora.toISOString() } };
  if (errorCode === "131042") return { pago: true };
  return {};
}

/**
 * La ventana más reciente de dos que llegaron por caminos distintos —la bandeja, el detalle, el
 * socket—. Sólo avanza con los mensajes del contacto, así que la más tardía es la vigente, y una
 * respuesta HTTP que tardó no pisa lo que ya trajo el socket.
 */
export function ventanaMasReciente(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

/** Un mensaje que se borró para todos (D10): la fila queda, y el panel dice que se eliminó. */
export function aplicarBorrado(
  rows: TimelineRow[],
  p: { messageId?: string | null; providerMessageId?: string | null; deletedAt?: string | null },
): TimelineRow[] {
  if (!p.messageId && !p.providerMessageId) return rows;
  let cambio = false;
  const siguiente = rows.map((r) => {
    if (r.kind !== "message") return r;
    const es =
      (!!p.messageId && r.payload.messageId === p.messageId) ||
      (!!p.providerMessageId && r.payload.providerMessageId === p.providerMessageId);
    if (!es) return r;
    cambio = true;
    return { ...r, payload: { ...r.payload, status: "deleted", deletedAt: p.deletedAt ?? new Date().toISOString() } };
  });
  return cambio ? siguiente : rows;
}

/** Si la fila es un mensaje borrado: lo dice el estado o la fecha, según de dónde vino la fila. */
export const estaBorrado = (payload: { status?: string; deletedAt?: string | null }): boolean =>
  payload.status === "deleted" || !!payload.deletedAt;
